import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Table, Button, Tag, Space, message } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { getPickListShipments } from 'services/printPickList';
import { /*exportShipmentPackingSlip,*/ exportShipmentSortList } from 'services/shipment';
import { nowTime, getProfileSettingValue } from 'utils';
import { userProfiles } from 'utils/auth';
import { useDispatch } from 'react-redux';
import { /*printPackingSlip,*/ printSortList } from 'actions/printActions';
import { Dispatch } from 'redux';
import { postPrintShipmentPackingSlip } from 'services/sales';
import { DEFAULT_PRINT_MODE } from 'constants/config';
// Task interface
interface Task {
  shipmentNum: number;
  shipmentId: string;
  fulfillmentOrderNum: number;
  fulfillmentOrderId: string;
  status: 'pending' | 'success' | 'failed' | 'waiting';
  message?: string;
}

// API response interface
interface ApiResponse {
  id: number;
  success: boolean;
  message?: string;
}

// Component props interface
type Props = {
  pick: any;
  sortArea: string;
  printer: any;
  onClose: () => void;
};

const TaskStatusModal = (props: Props) => {
  const dispatch: Dispatch<any> = useDispatch();
  const { sortArea, pick, printer, onClose } = props;
  // Initial tasks list
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);

  const getShipmentList = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPickListShipments({
        pickListNum: pick.pickListNum,
        sortArea,
      });
      setLoading(false);
      if (res.isSuccess) {
        setTasks(
          res.data.map((i: any) => {
            return {
              shipmentNum: i.shipmentNum,
              shipmentId: i.shipmentId,
              fulfillmentOrderNum: i.fulfillmentOrderNum,
              fulfillmentOrderId: i.fulfillmentOrderId,
              status: 'waiting',
            };
          })
        );
      }
    } catch (error) {
      setLoading(false);
    }
  }, [sortArea, pick]);

  useEffect(() => {
    getShipmentList();
  }, [getShipmentList]);

  useEffect(() => {
    console.log(printer);
  }, [printer]);

  const resetAllResultMessage = useCallback(() => {
    const temp: Task[] = tasks.map((i: Task) => {
      return {
        ...i,
        status: 'waiting',
        message: undefined,
      };
    });
    setTasks(temp);
    return temp;
  }, [tasks]);

  // Mock API call function
  const exportApiCall = async (
    task: Task,
    callback: (response: ApiResponse) => void
  ) => {
    try {
      //const res = await exportShipmentPackingSlip(
      const res = await exportShipmentSortList(
        task.fulfillmentOrderNum,
        task.shipmentNum,
        nowTime()
      );
      const pdfData = new Blob([res], { type: 'application/pdf' });
      const fileURL = window.URL.createObjectURL(pdfData);
      window.open(fileURL, `packingSlip(${task.shipmentId}).pdf`);
      callback({
        id: task.shipmentNum,
        success: true,
        message: 'Export successfully',
      });
    } catch (e) {
      console.log(`export packing slip error ${e}`);
      callback({
        id: task.shipmentNum,
        success: false,
        message: 'Export failed',
      });
    }
  };

  const printApiCall = async (
    task: Task,
    callback: (response: ApiResponse) => void
  ) => {
    const profile = userProfiles.profile;
    const printMode = await getProfileSettingValue(
      'PrintStationMode',
      DEFAULT_PRINT_MODE
    );
    const slip = {
      MasterAccountNum: profile.masterAccountNum,
      ProfileNum: profile.profileNum,
      PrinterNum: printer.printerNum,
      PrinterAccountNum: printer.printerAccountNum,
      User: profile.email,
      FulfillmentOrderNum: task.fulfillmentOrderNum,
      ShipmentNum: task.shipmentNum,
      Time: nowTime(),
    };
    if (printMode === DEFAULT_PRINT_MODE) {
      //dispatch(printPackingSlip(slip));
      dispatch(printSortList(slip));
      message.success('Print task generated successfully');
      callback({
        id: task.shipmentNum,
        success: true,
        message: 'print successfully',
      });
    } else {
      try {
        //const res = await postPrintShipmentPackingSlip(slip);
        await postPrintShipmentPackingSlip(slip);
        callback({
          id: task.shipmentNum,
          success: true,
          message: 'print successfully',
        });
      } catch (e) {
        callback({
          id: task.shipmentNum,
          success: false,
          message: 'print failed',
        });
      }
    }
  };

  // Process tasks sequentially without for loop
  const processTasks = async (taskList: Task[], type: string) => {
    const executeTask = (index: number): void => {
      if (index >= taskList.length) return;

      const currentTask = taskList[index];
      if (currentTask.status !== 'waiting') {
        return executeTask(index + 1);
      }

      // Set to pending
      setTasks((prev) =>
        prev.map((task) =>
          task.shipmentNum === currentTask.shipmentNum
            ? { ...task, status: 'pending' }
            : task
        )
      );

      const apiCall = type === 'print' ? printApiCall : exportApiCall;

      apiCall(currentTask, (response) => {
        try {
          setTasks((prev) =>
            prev.map((task) =>
              task.shipmentNum === currentTask.shipmentNum
                ? {
                    ...task,
                    status: response.success ? 'success' : 'failed',
                    message: response.message,
                  }
                : task
            )
          );
        } catch (error) {
          setTasks((prev) =>
            prev.map((task) =>
              task.shipmentNum === currentTask.shipmentNum
                ? { ...task, status: 'failed', message: 'Network error' }
                : task
            )
          );
        }

        // Process next task
        executeTask(index + 1);
      });
    };

    await executeTask(0);
  };

  // Table columns
  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'fulfillmentOrderId',
      key: 'fulfillmentOrderId',
    },
    {
      title: 'Shipment ID',
      dataIndex: 'shipmentId',
      key: 'shipmentId',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: Task) => (
        <Space>
          <Button
            disabled={tasks.some((t) => t.status === 'pending') || !printer}
            type="primary"
            key="packingSlip"
            onClick={() => {
              printApiCall(record, () => {});
            }}
          >
            Print Sort List
          </Button>
          <Button
            disabled={tasks.some((t) => t.status === 'pending')}
            key="packingSlip"
            onClick={() => {
              exportApiCall(record, () => {});
            }}
          >
            Export Sort List
          </Button>
        </Space>
      ),
    },
    {
      title: 'Result & Message',
      key: 'status',
      render: (_: any, record: Task) => (
        <>
          {record.status === 'waiting' && <Tag color="default">Waiting</Tag>}
          {record.status === 'pending' && (
            <Tag icon={<SyncOutlined spin />} color="processing">
              Processing
            </Tag>
          )}
          {record.status === 'success' && (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Success
            </Tag>
          )}
          {record.status === 'failed' && (
            <Tag icon={<CloseCircleOutlined />} color="error">
              Failed
            </Tag>
          )}
          {record.message && (
            <div style={{ marginTop: 8 }}>{record.message}</div>
          )}
        </>
      ),
    },
  ];

  return (
    <Modal
      title="Task Status"
      visible={true}
      onCancel={onClose}
      footer={[
        <Button
          type="primary"
          key="allPickList"
          onClick={() => {
            const tempTasks = resetAllResultMessage();
            setTimeout(() => {
              processTasks(tempTasks, 'print');
            }, 300);
          }}
          disabled={
            tasks.some((t) => t.status === 'pending') || loading || !printer
          }
        >
          Print All Sort List
        </Button>,
        <Button
          key="allpackingSlip"
          onClick={() => {
            const tempTasks = resetAllResultMessage();
            setTimeout(() => {
              processTasks(tempTasks, 'pdf');
            }, 300);
          }}
          disabled={tasks.some((t) => t.status === 'pending') || loading}
        >
          Export All Sort List As PDF
        </Button>,
        <Button
          key="close"
          onClick={onClose}
          disabled={tasks.some((t) => t.status === 'pending')}
        >
          Close
        </Button>,
      ]}
      width={950}
    >
      <Table
        columns={columns}
        dataSource={tasks}
        loading={loading}
        rowKey="shipmentNum"
        locale={{ emptyText: 'No tasks available' }}
        pagination={false}
      />
    </Modal>
  );
};

export default TaskStatusModal;
